/**
 * Machine-readable API error codes.
 *
 * Clients switch on these codes to decide whether to show a quota dialog, a
 * contextual paywall, a conflict resolver or a plain error. A generic server
 * error must never be shown for a condition that has a code here.
 */

export const ERROR_CODE = Object.freeze({
  // Generic
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Authentication and authorization
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  FORBIDDEN: 'FORBIDDEN',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  ADMIN_REQUIRED: 'ADMIN_REQUIRED',

  // Subscription and quotas
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  PLAYLIST_LIMIT_REACHED: 'PLAYLIST_LIMIT_REACHED',
  PLAYLIST_SONG_LIMIT_REACHED: 'PLAYLIST_SONG_LIMIT_REACHED',
  CUSTOM_SONG_LIMIT_REACHED: 'CUSTOM_SONG_LIMIT_REACHED',
  PRO_FEATURE_REQUIRED: 'PRO_FEATURE_REQUIRED',
  SUBSCRIPTION_STATUS_STALE: 'SUBSCRIPTION_STATUS_STALE',
  BILLING_SYNC_REQUIRED: 'BILLING_SYNC_REQUIRED',
  PURCHASE_NOT_FOUND: 'PURCHASE_NOT_FOUND',
  TRIAL_NOT_AVAILABLE: 'TRIAL_NOT_AVAILABLE',
  PLAYLIST_LOCKED: 'PLAYLIST_LOCKED',
  LEARN_UNIT_LOCKED: 'LEARN_UNIT_LOCKED',
  LEARN_DAILY_LIMIT_REACHED: 'LEARN_DAILY_LIMIT_REACHED',

  // Content
  DUPLICATE_PLAYLIST_ITEM: 'DUPLICATE_PLAYLIST_ITEM',
  REVISION_CONFLICT: 'REVISION_CONFLICT',
  CONTENT_TOO_LARGE: 'CONTENT_TOO_LARGE',
  INVALID_CHORD: 'INVALID_CHORD',
  INVALID_CHORDPRO: 'INVALID_CHORDPRO',
  ARRANGEMENT_NOT_EDITABLE: 'ARRANGEMENT_NOT_EDITABLE',
  MODERATION_REQUIRED: 'MODERATION_REQUIRED',
  RIGHTS_DECLARATION_REQUIRED: 'RIGHTS_DECLARATION_REQUIRED',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',

  // Client requirements
  // A property of the client BUILD rather than of the request, the account or
  // the content — which is why it is its own group.
  APP_UPDATE_REQUIRED: 'APP_UPDATE_REQUIRED',
});

export const ERROR_CODES = Object.freeze(Object.values(ERROR_CODE));

/** Error codes that should route the client to a contextual paywall. */
export const PAYWALL_ERROR_CODES = Object.freeze([
  ERROR_CODE.SUBSCRIPTION_REQUIRED,
  ERROR_CODE.PLAYLIST_LIMIT_REACHED,
  ERROR_CODE.PLAYLIST_SONG_LIMIT_REACHED,
  ERROR_CODE.CUSTOM_SONG_LIMIT_REACHED,
  ERROR_CODE.PRO_FEATURE_REQUIRED,
  ERROR_CODE.LEARN_UNIT_LOCKED,
  ERROR_CODE.LEARN_DAILY_LIMIT_REACHED,
]);

/** Maps a quota/entitlement error code to the paywall variant to display. */
export const ERROR_CODE_TO_PAYWALL_CONTEXT = Object.freeze({
  [ERROR_CODE.PLAYLIST_LIMIT_REACHED]: 'playlist_limit',
  [ERROR_CODE.PLAYLIST_SONG_LIMIT_REACHED]: 'playlist_song_limit',
  [ERROR_CODE.CUSTOM_SONG_LIMIT_REACHED]: 'custom_song_limit',
  [ERROR_CODE.SUBSCRIPTION_REQUIRED]: 'general_upgrade',
  [ERROR_CODE.PRO_FEATURE_REQUIRED]: 'general_upgrade',
  [ERROR_CODE.LEARN_UNIT_LOCKED]: 'learn_unit_locked',
  [ERROR_CODE.LEARN_DAILY_LIMIT_REACHED]: 'learn_daily_limit',
});

/**
 * Maps a Premium feature identifier to its paywall variant.
 *
 * `tuner_alternate_tunings` has an entry even though nothing on the server ever
 * throws for it. The tuner gate is entirely client-side — preferences are stored
 * without an entitlement check, and the resolution is what gets gated (see
 * `resolveTunerTuning` in the chord engine) — but the clients still need one
 * canonical feature → context lookup, and this is where every other feature
 * keeps it.
 */
export const FEATURE_TO_PAYWALL_CONTEXT = Object.freeze({
  offline_catalog: 'offline_required',
  advanced_editor: 'advanced_editor_required',
  revision_history: 'revision_history_required',
  chordpro_import_export: 'advanced_editor_required',
  plain_text_import: 'advanced_editor_required',
  learn_full_curriculum: 'learn_unit_locked',
  learn_unlimited_practice: 'learn_daily_limit',
  tuner_alternate_tunings: 'tuner_tuning_locked',
});

/** Default HTTP status for each error code. */
export const ERROR_STATUS = Object.freeze({
  [ERROR_CODE.INTERNAL_ERROR]: 500,
  [ERROR_CODE.VALIDATION_ERROR]: 422,
  [ERROR_CODE.NOT_FOUND]: 404,
  [ERROR_CODE.BAD_REQUEST]: 400,
  [ERROR_CODE.RATE_LIMITED]: 429,
  [ERROR_CODE.PAYLOAD_TOO_LARGE]: 413,
  [ERROR_CODE.SERVICE_UNAVAILABLE]: 503,

  [ERROR_CODE.UNAUTHENTICATED]: 401,
  [ERROR_CODE.INVALID_TOKEN]: 401,
  [ERROR_CODE.TOKEN_EXPIRED]: 401,
  [ERROR_CODE.EMAIL_NOT_VERIFIED]: 403,
  [ERROR_CODE.FORBIDDEN]: 403,
  [ERROR_CODE.ACCOUNT_SUSPENDED]: 403,
  [ERROR_CODE.ACCOUNT_DELETED]: 403,
  [ERROR_CODE.ADMIN_REQUIRED]: 403,

  [ERROR_CODE.SUBSCRIPTION_REQUIRED]: 402,
  [ERROR_CODE.PLAYLIST_LIMIT_REACHED]: 402,
  [ERROR_CODE.PLAYLIST_SONG_LIMIT_REACHED]: 402,
  [ERROR_CODE.CUSTOM_SONG_LIMIT_REACHED]: 402,
  [ERROR_CODE.PRO_FEATURE_REQUIRED]: 402,
  [ERROR_CODE.SUBSCRIPTION_STATUS_STALE]: 409,
  [ERROR_CODE.BILLING_SYNC_REQUIRED]: 409,
  [ERROR_CODE.PURCHASE_NOT_FOUND]: 404,
  [ERROR_CODE.TRIAL_NOT_AVAILABLE]: 409,
  [ERROR_CODE.PLAYLIST_LOCKED]: 403,
  [ERROR_CODE.LEARN_UNIT_LOCKED]: 402,
  [ERROR_CODE.LEARN_DAILY_LIMIT_REACHED]: 402,

  [ERROR_CODE.DUPLICATE_PLAYLIST_ITEM]: 409,
  [ERROR_CODE.REVISION_CONFLICT]: 409,
  [ERROR_CODE.CONTENT_TOO_LARGE]: 413,
  [ERROR_CODE.INVALID_CHORD]: 422,
  [ERROR_CODE.INVALID_CHORDPRO]: 422,
  [ERROR_CODE.ARRANGEMENT_NOT_EDITABLE]: 403,
  [ERROR_CODE.MODERATION_REQUIRED]: 409,
  [ERROR_CODE.RIGHTS_DECLARATION_REQUIRED]: 422,
  [ERROR_CODE.ALREADY_EXISTS]: 409,
  [ERROR_CODE.UPLOAD_FAILED]: 500,
  [ERROR_CODE.UNSUPPORTED_MEDIA_TYPE]: 415,

  // RFC 9110 "Upgrade Required": the server refuses to serve this client until
  // it upgrades. A deliberate spec deviation — no `Upgrade` header is sent,
  // because there is no HTTP protocol to upgrade to; the app build is the
  // protocol. 403 was rejected: it already means CORS rejection and role
  // failure, and overloading it would make an update prompt indistinguishable
  // from a permissions bug in every dashboard.
  [ERROR_CODE.APP_UPDATE_REQUIRED]: 426,
});

/** HTTP status for a code, defaulting to 500. */
export function statusForErrorCode(code) {
  return ERROR_STATUS[code] ?? 500;
}

/** True when the client should present a paywall rather than an error toast. */
export function isPaywallError(code) {
  return PAYWALL_ERROR_CODES.includes(code);
}

/** Paywall variant for an error payload, or `null` when not a paywall error. */
export function paywallContextForError(code, details = {}) {
  if (details?.upgradeFeature && FEATURE_TO_PAYWALL_CONTEXT[details.upgradeFeature]) {
    return FEATURE_TO_PAYWALL_CONTEXT[details.upgradeFeature];
  }
  return ERROR_CODE_TO_PAYWALL_CONTEXT[code] ?? null;
}
